import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { Cpu, Plus, X, Trash2, Pencil, Search, Download, Upload, ImagePlus, FileSpreadsheet, FileText, ChevronDown, FileUp, AlertTriangle, Send, PackagePlus, Wrench, MapPin, CheckCircle2, ShoppingCart } from "lucide-react";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SpareMaster() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stockTxnFor, setStockTxnFor] = useState(null);
  const [maintFor, setMaintFor] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestMsg, setDigestMsg] = useState("");
  const [reorderBusy, setReorderBusy] = useState("");

  const reorderToSupplier = async (s) => {
    const suggested = Math.max(1, (s.reorder_level * 2 || 2) - s.stock_quantity);
    if (!window.confirm(`Create a Supplier Daily Order for ${suggested} × ${s.part_name}${s.supplier_name ? ` from ${s.supplier_name}` : ""}? You can edit it afterwards.`)) return;
    setReorderBusy(s.id); setErr("");
    try {
      const today = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const r = await api.post("/vendor-orders", {
        order_date: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`,
        vendor_name: s.supplier_name || "To be decided",
        items: [{ spare_id: s.id, part_name: s.part_name, part_number: s.part_number || null, brand: s.brand || null, quantity: suggested, unit_price: 0 }],
        notes: `Auto-reorder: ${s.part_id} low stock (${s.stock_quantity} ≤ reorder ${s.reorder_level})`,
        status: "draft",
      });
      setDigestMsg(`Reorder created: ${r.data.order_no} — ${suggested} × ${s.part_name} (draft in Supplier Daily Order)`);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setReorderBusy(""); }
  };
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (q) params.set("q", q);
      if (onlyLow) params.set("low_stock", "true");
      const [r, c] = await Promise.all([
        api.get(`/spares${params.toString() ? `?${params}` : ""}`),
        api.get("/spares/categories"),
      ]);
      setItems(r.data); setCategories(c.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [status, onlyLow]);

  const lowCount = items.filter((s) => s.low_stock).length;

  const filteredIds = items.map((x) => x.id);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someSelected = filteredIds.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) filteredIds.forEach((id) => n.delete(id));
    else filteredIds.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const arr = Array.from(selected);
    if (!window.confirm(`Delete ${arr.length} spare(s)?`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(arr, (id) => api.delete(`/spares/${id}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < arr.length) setErr(`${arr.length - ok} deletion(s) failed.`);
  };

  const downloadWithAuth = async (path, filename) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Catalogue</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Spare Master</h1>
          <p className="mt-1 text-sm text-muted-foreground">Complete parts catalogue with warranty and images.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="spare-digest-send"
            disabled={digestBusy}
            onClick={async () => {
              setDigestBusy(true); setDigestMsg(""); setErr("");
              try {
                const r = await api.post("/spares/send-low-stock-digest");
                const d = r.data || {};
                if (d.items === 0) setDigestMsg("No low-stock items to report.");
                else if (d.sent > 0) setDigestMsg(`Digest sent to ${d.sent} admin${d.sent > 1 ? "s" : ""} covering ${d.items} item${d.items > 1 ? "s" : ""}.`);
                else setDigestMsg(d.reason === "no-admin-recipients" ? "No admin recipients configured." : "Digest not sent.");
              } catch (e) { setErr(formatApiError(e)); }
              finally { setDigestBusy(false); }
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
          >
            <Send className="h-3.5 w-3.5" /> {digestBusy ? "Sending…" : "Send digest"}
          </button>
          <button
            data-testid="spare-import-open"
            onClick={() => setImporting(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-2 text-xs font-semibold hover:bg-secondary"
          >
            <Upload className="h-3.5 w-3.5" /> Import
          </button>

          {/* Export dropdown */}
          <div className="relative">
            <button
              data-testid="spare-export-toggle"
              onClick={() => setExportOpen((v) => !v)}
              onBlur={() => setTimeout(() => setExportOpen(false), 150)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-2 text-xs font-semibold hover:bg-secondary"
            >
              <Download className="h-3.5 w-3.5" /> Export
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {exportOpen && (
              <div className="absolute right-0 z-20 mt-1 w-44 overflow-hidden rounded-md border border-border/70 bg-card shadow-lg">
                <button
                  data-testid="spare-export-csv"
                  onClick={() => { setExportOpen(false); downloadWithAuth("/spares/export?fmt=csv", "spare-master.csv"); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs hover:bg-secondary"
                >
                  <FileText className="h-3.5 w-3.5" /> Export CSV
                </button>
                <button
                  data-testid="spare-export-xlsx"
                  onClick={() => { setExportOpen(false); downloadWithAuth("/spares/export?fmt=xlsx", "spare-master.xlsx"); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs hover:bg-secondary"
                >
                  <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-500" /> Export Excel
                </button>
                <button
                  data-testid="spare-export-pdf"
                  onClick={() => { setExportOpen(false); downloadWithAuth("/spares/export?fmt=pdf", "spare-master.pdf"); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs hover:bg-secondary"
                >
                  <FileText className="h-3.5 w-3.5 text-rose-500" /> Export PDF
                </button>
              </div>
            )}
          </div>

          <button data-testid="spare-create-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
            <Plus className="h-3.5 w-3.5" /> Add spare
          </button>
        </div>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
      {digestMsg && <div data-testid="spare-digest-msg" className="mb-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600">{digestMsg}</div>}

      <div className="mb-3 flex flex-wrap gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="spare-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by part id, name, brand, model, part no…"
            className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <button onClick={load} className="rounded-md border border-border/70 px-3 py-2 text-xs font-medium">Search</button>
        <select data-testid="spare-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-xs">
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button
          data-testid="spare-low-filter"
          onClick={() => setOnlyLow((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-semibold ${onlyLow ? "border-rose-500 bg-rose-500/10 text-rose-600" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          {onlyLow ? "Showing low stock" : "Low stock only"}
          {!onlyLow && lowCount > 0 && (
            <span className="ml-1 rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{lowCount}</span>
          )}
        </button>
      </div>

      <div className="sharp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-secondary/30 text-left">
              <tr>
                <th className="w-10 px-4 py-2">
                  <RowCheckbox testid="spare-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
                </th>
                <th className="px-4 py-2 label-eyebrow">Image</th>
                <th className="px-4 py-2 label-eyebrow">Part ID</th>
                <th className="px-4 py-2 label-eyebrow">Name</th>
                <th className="px-4 py-2 label-eyebrow">Brand</th>
                <th className="px-4 py-2 label-eyebrow">Compatible</th>
                <th className="px-4 py-2 label-eyebrow">Model / Part No</th>
                <th className="px-4 py-2 label-eyebrow">Stock</th>
                <th className="px-4 py-2 label-eyebrow">Location</th>
                <th className="px-4 py-2 label-eyebrow">Condition</th>
                <th className="px-4 py-2 label-eyebrow">Status</th>
                <th className="px-4 py-2 label-eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} data-testid={`spare-row-${s.id}`} className="border-b border-border/40 hover:bg-secondary/40">
                  <td className="px-4 py-2">
                    <RowCheckbox testid={`spare-select-${s.id}`} checked={selected.has(s.id)} onChange={() => toggleOne(s.id)} />
                  </td>
                  <td className="px-4 py-2">
                    {s.images?.[0] ? (
                      <ImgAuth id={s.images[0]} />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-md border border-dashed border-border/60 text-muted-foreground">
                        <Cpu className="h-4 w-4" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs font-semibold text-primary">{s.part_id}</td>
                  <td className="px-4 py-2 font-medium">{s.part_name}</td>
                  <td className="px-4 py-2 text-muted-foreground">{s.brand || "—"}</td>
                  <td className="px-4 py-2 max-w-xs truncate text-xs text-muted-foreground">{s.compatible_devices || "—"}</td>
                  <td className="px-4 py-2 font-mono text-[11px]">{[s.model_number, s.part_number].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-4 py-2 font-semibold">
                    <div className="flex items-center gap-2">
                      <span data-testid={`spare-available-${s.id}`}>{s.available_qty}<span className="text-[10px] text-muted-foreground">/{s.stock_quantity} {s.unit || "pcs"}</span></span>
                      {s.ready ? (
                        <span data-testid={`spare-ready-${s.id}`} title="Ready for deployment" className="inline-flex items-center gap-0.5 rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-600"><CheckCircle2 className="h-2.5 w-2.5" /> Ready</span>
                      ) : (
                        <span data-testid={`spare-notready-${s.id}`} title="Not ready (no available units or out of service)" className="inline-flex rounded-md bg-slate-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-500">Not ready</span>
                      )}
                      {s.low_stock && (
                        <span
                          data-testid={`spare-low-badge-${s.id}`}
                          className="inline-flex items-center gap-0.5 rounded-md bg-rose-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-rose-600"
                          title={`Below reorder level (${s.reorder_level})`}
                        >
                          <AlertTriangle className="h-2.5 w-2.5" /> Low
                        </span>
                      )}
                      {s.low_stock && (
                        <button
                          data-testid={`spare-reorder-${s.id}`}
                          onClick={() => reorderToSupplier(s)}
                          disabled={reorderBusy === s.id}
                          title={`One-click reorder${s.supplier_name ? ` from ${s.supplier_name}` : ""}`}
                          className="inline-flex items-center gap-0.5 rounded-md border border-primary/50 bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary hover:bg-primary/20 disabled:opacity-50"
                        >
                          <ShoppingCart className="h-2.5 w-2.5" /> {reorderBusy === s.id ? "…" : "Reorder"}
                        </button>
                      )}
                    </div>
                    {(s.unavailable_qty > 0 || s.reorder_level > 0) && (
                      <div className="text-[10px] text-muted-foreground">
                        {s.unavailable_qty > 0 ? `${s.unavailable_qty} in maintenance · ` : ""}{s.reorder_level > 0 ? `Reorder ≤ ${s.reorder_level}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {[s.warehouse, s.aisle, s.rack, s.shelf, s.bin].some(Boolean) ? (
                      <span data-testid={`spare-location-${s.id}`} className="inline-flex items-center gap-1 font-mono text-[11px] text-muted-foreground" title="Warehouse / Aisle / Rack / Shelf / Bin">
                        <MapPin className="h-3 w-3 text-primary" />
                        {[s.warehouse, s.aisle, s.rack, s.shelf, s.bin].map((v) => v || "·").join("/")}
                      </span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    <span data-testid={`spare-condition-${s.id}`} className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                      s.condition === "out_of_service" ? "bg-rose-500/10 text-rose-600"
                      : s.condition === "needs_repair" ? "bg-amber-500/10 text-amber-600"
                      : s.condition === "used" ? "bg-blue-500/10 text-blue-500"
                      : "bg-emerald-500/10 text-emerald-600"}`}>
                      {(s.condition || "new").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${s.status === "active" ? "bg-emerald-500/10 text-emerald-500" : "bg-slate-500/10 text-slate-500"}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <button data-testid={`spare-stock-txn-${s.id}`} title="Add / remove stock" onClick={() => setStockTxnFor(s)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-emerald-600"><PackagePlus className="h-3.5 w-3.5" /></button>
                      <button data-testid={`spare-maintenance-${s.id}`} title="Condition & maintenance" onClick={() => setMaintFor(s)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-amber-600"><Wrench className="h-3.5 w-3.5" /></button>
                      <button data-testid={`spare-edit-${s.id}`} onClick={() => setEditing(s)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`spare-delete-${s.id}`}
                        onClick={async () => { if (!window.confirm(`Delete ${s.part_id}?`)) return; try { await api.delete(`/spares/${s.id}`); await load(); } catch (er) { setErr(formatApiError(er)); } }}
                        className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={13} className="px-4 py-10 text-center text-xs text-muted-foreground">No spares in catalogue.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <BulkBar count={selected.size} entityLabel="spares" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && <SpareModal categories={categories} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <SpareModal categories={categories} existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {importing && <ImportModal onClose={() => setImporting(false)} onDone={() => { setImporting(false); load(); }} downloadWithAuth={downloadWithAuth} />}
      {stockTxnFor && <StockTxnModal spare={stockTxnFor} onClose={() => setStockTxnFor(null)} onSaved={() => { setStockTxnFor(null); load(); }} />}
      {maintFor && <MaintenanceModal spare={maintFor} onClose={() => setMaintFor(null)} onSaved={() => { setMaintFor(null); load(); }} />}
    </div>
  );
}

// Small helper: fetches an authenticated image as blob URL (works even when cookies aren't sent)
function ImgAuth({ id }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true; let url = null;
    api.get(`/files/${id}`, { responseType: "blob" })
      .then((r) => { if (!alive) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [id]);
  return src ? (
    <img src={src} alt="" className="h-10 w-10 rounded-md border border-border/60 object-cover" />
  ) : (
    <div className="h-10 w-10 animate-pulse rounded-md bg-secondary/50" />
  );
}

function SpareModal({ categories, onClose, onSaved, existing }) {
  const [form, setForm] = useState({
    part_name: existing?.part_name || "",
    category: existing?.category || (categories[0] || "Motherboard"),
    brand: existing?.brand || "",
    compatible_devices: existing?.compatible_devices || "",
    model_number: existing?.model_number || "",
    part_number: existing?.part_number || "",
    warranty: existing?.warranty || "",
    stock_quantity: existing?.stock_quantity ?? 0,
    reorder_level: existing?.reorder_level ?? 0,
    unit: existing?.unit || "pcs",
    description: existing?.description || "",
    status: existing?.status || "active",
    condition: existing?.condition || "new",
    warehouse: existing?.warehouse || "",
    aisle: existing?.aisle || "",
    rack: existing?.rack || "",
    shelf: existing?.shelf || "",
    bin: existing?.bin || "",
    supplier_name: existing?.supplier_name || "",
    supplier_details: existing?.supplier_details || "",
  });
  // Only one image per spare (per user requirement)
  const [imageId, setImageId] = useState(existing?.images?.[0] || null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "photo");
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageId(r.data.id);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...form, stock_quantity: Number(form.stock_quantity), reorder_level: Number(form.reorder_level), images: imageId ? [imageId] : [] };
      if (existing) await api.patch(`/spares/${existing.id}`, payload);
      else await api.post("/spares", payload);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl sharp-card max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3 sticky top-0 bg-card z-10">
          <div>
            <p className="label-eyebrow text-primary">{existing ? existing.part_id : "New spare"}</p>
            <h3 className="mt-1 font-heading text-lg font-semibold">{existing ? `Edit — ${existing.part_name}` : "Add new spare"}</h3>
          </div>
          <button data-testid="spare-modal-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Part name *</label>
            <input data-testid="spare-form-name" required value={form.part_name} onChange={set("part_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Compatible devices</label>
            <input data-testid="spare-form-compat" value={form.compatible_devices} onChange={set("compatible_devices")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="e.g. HP EliteBook 840 G7, Dell Latitude 5400" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Model number</label>
            <input data-testid="spare-form-model" value={form.model_number} onChange={set("model_number")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Part number</label>
            <input data-testid="spare-form-partno" value={form.part_number} onChange={set("part_number")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Warranty</label>
            <input data-testid="spare-form-warranty" value={form.warranty} onChange={set("warranty")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="e.g. 1 year, 6 months" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Stock quantity</label>
            <input data-testid="spare-form-stock" type="number" min="0" value={form.stock_quantity} onChange={set("stock_quantity")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Reorder level</label>
            <input data-testid="spare-form-reorder" type="number" min="0" value={form.reorder_level} onChange={set("reorder_level")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="0 = disabled" />
            <p className="mt-1 text-[10px] text-muted-foreground">Alerts fire when stock ≤ reorder level. Leave 0 to disable.</p>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Unit</label>
            <input data-testid="spare-form-unit" value={form.unit} onChange={set("unit")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="pcs, kg, box" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Status</label>
            <select data-testid="spare-form-status" value={form.status} onChange={set("status")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Condition</label>
            <select data-testid="spare-form-condition" value={form.condition} onChange={set("condition")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="new">New</option>
              <option value="used">Used</option>
              <option value="needs_repair">Needs Repair</option>
              <option value="out_of_service">Out of Service</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Storage location (Warehouse / Aisle / Rack / Shelf / Bin)</label>
            <div className="grid grid-cols-5 gap-2">
              <input data-testid="spare-form-warehouse" value={form.warehouse} onChange={set("warehouse")} placeholder="WH" className="rounded-md border border-input bg-background px-2 py-2 text-sm" />
              <input data-testid="spare-form-aisle" value={form.aisle} onChange={set("aisle")} placeholder="Aisle" className="rounded-md border border-input bg-background px-2 py-2 text-sm" />
              <input data-testid="spare-form-rack" value={form.rack} onChange={set("rack")} placeholder="Rack" className="rounded-md border border-input bg-background px-2 py-2 text-sm" />
              <input data-testid="spare-form-shelf" value={form.shelf} onChange={set("shelf")} placeholder="Shelf" className="rounded-md border border-input bg-background px-2 py-2 text-sm" />
              <input data-testid="spare-form-bin" value={form.bin} onChange={set("bin")} placeholder="Bin" className="rounded-md border border-input bg-background px-2 py-2 text-sm" />
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">Tip: keep heavy items on lower shelves.</p>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Supplier name</label>
            <input data-testid="spare-form-supplier" value={form.supplier_name} onChange={set("supplier_name")} placeholder="e.g. ABC Distributors" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Supplier details</label>
            <input data-testid="spare-form-supplier-details" value={form.supplier_details} onChange={set("supplier_details")} placeholder="phone, email, GST, address…" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Description</label>
            <textarea data-testid="spare-form-desc" value={form.description} onChange={set("description")} rows={2} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Image (1 per spare)</label>
            <div className="flex flex-wrap items-center gap-3">
              {imageId ? (
                <div className="relative">
                  <ImgAuthLarge id={imageId} />
                  <button
                    type="button"
                    data-testid="spare-form-image-remove"
                    onClick={() => setImageId(null)}
                    className="absolute -right-1.5 -top-1.5 rounded-full border border-border bg-card p-1 text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-dashed border-border/70 px-4 py-3 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary">
                  <ImagePlus className="h-4 w-4" />
                  {uploading ? "Uploading…" : "Upload image"}
                  <input data-testid="spare-form-image" type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                </label>
              )}
              {imageId && (
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border/70 px-3 py-2 text-xs text-muted-foreground hover:text-primary">
                  <ImagePlus className="h-3.5 w-3.5" />
                  Replace
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                </label>
              )}
            </div>
          </div>
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="spare-form-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (existing ? "Save changes" : "Create spare")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImgAuthLarge({ id }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true; let url = null;
    api.get(`/files/${id}`, { responseType: "blob" })
      .then((r) => { if (!alive) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [id]);
  return src ? (
    <img src={src} alt="Spare" className="h-24 w-24 rounded-md border border-border/60 object-cover" />
  ) : (
    <div className="h-24 w-24 animate-pulse rounded-md bg-secondary/50" />
  );
}

function ImportModal({ onClose, onDone, downloadWithAuth }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/spares/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <div>
            <p className="label-eyebrow text-primary">Bulk upload</p>
            <h3 className="mt-1 font-heading text-lg font-semibold">Import Spares</h3>
          </div>
          <button data-testid="spare-import-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4 p-5">
          <div className="rounded-md border border-border/60 bg-secondary/30 p-3 text-xs text-muted-foreground">
            <p>Upload an Excel (.xlsx) or CSV (.csv) file. Required column: <b>Part Name</b>.</p>
            <button
              type="button"
              data-testid="spare-import-template"
              onClick={() => downloadWithAuth("/spares/template.xlsx", "spare-master-template.xlsx")}
              className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-card px-2.5 py-1.5 text-[11px] font-semibold hover:bg-secondary"
            >
              <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-500" /> Download template
            </button>
          </div>

          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border/70 px-4 py-8 text-center hover:border-primary/60">
            <FileUp className="h-6 w-6 text-muted-foreground" />
            <span className="text-sm font-medium">{file ? file.name : "Choose .xlsx or .csv file"}</span>
            <span className="text-[11px] text-muted-foreground">Up to 10 MB</span>
            <input
              data-testid="spare-import-file"
              type="file"
              accept=".xlsx,.csv"
              className="hidden"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            />
          </label>

          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

          {result && (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs">
              <p className="font-semibold text-emerald-600">Imported {result.created} of {result.total_rows} rows.</p>
              {result.errors?.length > 0 && (
                <div className="mt-1 max-h-32 overflow-y-auto text-destructive">
                  <p className="font-semibold">Skipped rows:</p>
                  <ul className="list-disc pl-4">
                    {result.errors.map((er, i) => <li key={i}>Row {er.row}: {er.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            {result ? (
              <button type="button" data-testid="spare-import-done" onClick={onDone} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90">Done</button>
            ) : (
              <>
                <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
                <button
                  data-testid="spare-import-submit"
                  disabled={!file || busy}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                >
                  {busy ? "Uploading…" : "Import"}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function StockTxnModal({ spare, onClose, onSaved }) {
  const [txnType, setTxnType] = useState("add");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [txns, setTxns] = useState([]);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const loadTxns = () => api.get(`/spares/${spare.id}/stock-txns`).then((r) => setTxns(r.data)).catch(() => {});
  useEffect(() => { loadTxns(); }, [spare.id]); // reload when target spare changes

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post(`/spares/${spare.id}/stock-txn`, { txn_type: txnType, quantity: Number(qty), note });
      onSaved();
    } catch (er) { setErr(formatApiError(er)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg sharp-card max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <div>
            <p className="label-eyebrow text-primary">{spare.part_id} — current stock: {spare.stock_quantity} {spare.unit || "pcs"}</p>
            <h3 className="mt-1 font-heading text-lg font-semibold">Stock transaction — {spare.part_name}</h3>
          </div>
          <button data-testid="stock-txn-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5">
          <div className="flex gap-2">
            <button type="button" data-testid="stock-txn-add" onClick={() => setTxnType("add")}
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${txnType === "add" ? "border-emerald-500 bg-emerald-500/10 text-emerald-600" : "border-border/70 text-muted-foreground"}`}>+ Add stock</button>
            <button type="button" data-testid="stock-txn-remove" onClick={() => setTxnType("remove")}
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${txnType === "remove" ? "border-rose-500 bg-rose-500/10 text-rose-600" : "border-border/70 text-muted-foreground"}`}>− Remove stock</button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow mb-1.5 block">Quantity *</label>
              <input data-testid="stock-txn-qty" required type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Note</label>
              <input data-testid="stock-txn-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. GRN #123 / issued to Ramesh" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
          </div>
          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex justify-end">
            <button data-testid="stock-txn-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (txnType === "add" ? "Add to stock" : "Remove from stock")}
            </button>
          </div>
        </form>
        <div className="border-t border-border/60 px-5 py-3">
          <p className="label-eyebrow mb-2">Transaction history</p>
          <div className="max-h-52 space-y-1.5 overflow-y-auto">
            {txns.map((t) => (
              <div key={t.id} data-testid={`stock-txn-row-${t.id}`} className="flex items-center justify-between rounded-md border border-border/50 px-3 py-1.5 text-xs">
                <span>
                  <b className={t.txn_type === "add" ? "text-emerald-600" : "text-rose-600"}>{t.txn_type === "add" ? "+" : "−"}{t.quantity}</b>
                  <span className="text-muted-foreground"> ({t.prev_stock} → {t.new_stock})</span>
                  {t.note && <span className="text-muted-foreground"> · {t.note}</span>}
                </span>
                <span className="shrink-0 text-[10px] text-muted-foreground">{t.by_name || "—"} · {t.at ? new Date(t.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>
              </div>
            ))}
            {txns.length === 0 && <p className="py-3 text-center text-xs text-muted-foreground">No transactions yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function MaintenanceModal({ spare, onClose, onSaved }) {
  const [condition, setCondition] = useState(spare.condition || "new");
  const [unavailable, setUnavailable] = useState(spare.unavailable_qty ?? 0);
  const [note, setNote] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post(`/spares/${spare.id}/maintenance`, { condition, unavailable_qty: Number(unavailable), note });
      onSaved();
    } catch (er) { setErr(formatApiError(er)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg sharp-card max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <div>
            <p className="label-eyebrow text-primary">{spare.part_id} — stock {spare.stock_quantity} {spare.unit || "pcs"}</p>
            <h3 className="mt-1 font-heading text-lg font-semibold">Condition &amp; maintenance — {spare.part_name}</h3>
          </div>
          <button data-testid="maint-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5">
          <div>
            <label className="label-eyebrow mb-1.5 block">Condition / status</label>
            <select data-testid="maint-condition" value={condition} onChange={(e) => setCondition(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="new">New</option>
              <option value="used">Used</option>
              <option value="needs_repair">Needs Repair</option>
              <option value="out_of_service">Out of Service</option>
            </select>
            {condition === "out_of_service" && <p className="mt-1 text-[11px] text-rose-600">Out-of-service items are never &ldquo;Ready for deployment&rdquo;.</p>}
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Units in maintenance / damaged (not available)</label>
            <input data-testid="maint-unavailable" type="number" min="0" max={spare.stock_quantity} value={unavailable} onChange={(e) => setUnavailable(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            <p className="mt-1 text-[10px] text-muted-foreground">Available = total stock − this number.</p>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Service note</label>
            <input data-testid="maint-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. 2 units sent to service centre" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex justify-end">
            <button data-testid="maint-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : "Save maintenance record"}
            </button>
          </div>
        </form>
        {(spare.maintenance_log || []).length > 0 && (
          <div className="border-t border-border/60 px-5 py-3">
            <p className="label-eyebrow mb-2">Maintenance log</p>
            <div className="max-h-48 space-y-1.5 overflow-y-auto">
              {spare.maintenance_log.map((m, i) => (
                <div key={i} className="rounded-md border border-border/50 px-3 py-1.5 text-xs">
                  <b className="capitalize">{(m.condition || "").replace(/_/g, " ")}</b>
                  <span className="text-muted-foreground"> · {m.unavailable_qty} unavailable{m.note ? ` · ${m.note}` : ""} · {m.by_name || "—"} · {m.at ? new Date(m.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
